import React from 'react';
import { Dot } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';

import card from './RoomOptionCards.module.scss';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { ConfiguratorGroupDto } from '@/services/configurator';
import { BuyerOptionCardPrice } from '../../../BuyerOptionCard/BuyerOptionCardPrice';
import { useImageUrls } from '../../../useImageUrls';
import { groupSelectedOption } from './helpers';

interface GroupCardProps {
    group: ConfiguratorGroupDto;
    onClick: () => void;
}

// A single option group rendered as a two-column card (image left, info right) in the centered card
// grid. The list-row equivalent is `GroupRow`; both surface the same info (image, name, current
// selection, price). Clicking opens the group's option-selection view.
export const GroupCard: React.FC<GroupCardProps> = ({ group, onClick }) => {
    const { getProjectCatalogProductFileUrl, getProjectCatalogProductFileThumbnailUrl } = useImageUrls();
    const selectedOption = groupSelectedOption(group);
    const defaultOption = group.options.find((o) => o.isDefault) ?? null;
    const imageOption = selectedOption ?? defaultOption;
    const selected = !!selectedOption;

    return (
        <button type="button" className={card.card} onClick={onClick}>
            <div className={card.media}>
                <ProductThumbnails
                    imageUrlGenerator={getProjectCatalogProductFileUrl}
                    thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                    images={imageOption?.product?.images || []}
                    hideImageCount
                    size={96}
                    className={card.thumbnail}
                />
            </div>
            <div className={card.info}>
                <div className={card.topLine}>
                    <span className={card.name} title={group.name}>
                        {group.name}
                    </span>
                    <FontAwesomeIcon className={card.chevron} icon={faChevronRight} />
                </div>
                <div className={card.selection} title={selectedOption?.productName ?? undefined}>
                    <Dot variant={selected ? 'primary' : 'warning'} />
                    <span className={card.selectionName}>
                        {selected ? selectedOption?.productName : <Trans>None selected</Trans>}
                    </span>
                </div>
                {selectedOption && (
                    <div className={card.price}>
                        <BuyerOptionCardPrice option={selectedOption} compact />
                    </div>
                )}
            </div>
        </button>
    );
};
